import {
  AcceptanceFieldRule,
  AcceptanceRuntimeFieldBinding,
  AcceptanceRuntimeFieldCoverage,
} from './types';

const normalizedLabel = (value: string): string => value.replace(/\s+/g, '').trim().toLowerCase();

const uniqueBy = <T,>(items: T[], keyOf: (item: T) => string): T[] => (
  Array.from(new Map(items.map(item => [keyOf(item), item])).values())
);

export const requiredUnmountedProjectedFieldIds = (
  projectedFields: AcceptanceFieldRule[],
  mountedFieldIds: Iterable<string>,
): string[] => {
  const mounted = new Set(mountedFieldIds);
  return projectedFields
    .filter(field => !field.conditionallyMounted)
    .map(field => field.fieldId)
    .filter(fieldId => !mounted.has(fieldId));
};

export const mergeAcceptanceRuntimeFieldCoverage = (
  previous: AcceptanceRuntimeFieldCoverage | undefined,
  current: AcceptanceRuntimeFieldCoverage,
  projectedFields: AcceptanceFieldRule[],
): AcceptanceRuntimeFieldCoverage => {
  if (!previous) {
    return {
      ...current,
      unmountedProjectedFieldIds: requiredUnmountedProjectedFieldIds(
        projectedFields,
        current.modeledFields.map(field => field.fieldId),
      ),
    };
  }

  const bindings = uniqueBy(
    [...previous.bindings, ...current.bindings],
    binding => binding.bindingId,
  );
  const modeledFields = uniqueBy(
    [...previous.modeledFields, ...current.modeledFields],
    field => field.fieldId,
  );
  const boundFieldIdsByLabel = new Map<string, Set<string>>();
  bindings.forEach(binding => {
    const key = normalizedLabel(binding.label);
    const fields = boundFieldIdsByLabel.get(key) || new Set<string>();
    fields.add(binding.fieldId);
    boundFieldIdsByLabel.set(key, fields);
  });
  const unmodeledFields = uniqueBy(
    [...previous.unmodeledFields, ...current.unmodeledFields],
    field => field.fieldId,
  ).filter(field => {
    const fields = boundFieldIdsByLabel.get(normalizedLabel(field.label));
    return !fields || fields.size !== 1;
  });
  const modeledFieldIds = new Set(modeledFields.map(field => field.fieldId));
  const projectedCounts = new Map<string, number>();
  projectedFields.forEach(field => {
    projectedCounts.set(field.fieldId, (projectedCounts.get(field.fieldId) || 0) + 1);
  });

  return {
    candidateCount: bindings.length + unmodeledFields.length,
    modeledFields,
    unmodeledFields,
    unmountedProjectedFieldIds: requiredUnmountedProjectedFieldIds(projectedFields, modeledFieldIds),
    duplicateProjectedFieldIds: Array.from(projectedCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([fieldId]) => fieldId),
    bindings,
  };
};

export const bindingFieldIds = (
  bindings: AcceptanceRuntimeFieldBinding[],
): string[] => Array.from(new Set(bindings.map(binding => binding.fieldId)));
